import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { LicenseStatusBadge } from "@/components/LicenseStatusBadge"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CopyField } from "@/components/ui/copy-field"
import { DataTable, DataRow } from "@/components/ui/data-table"
import { SubNav } from "@/components/ui/sub-nav"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

interface DeviceRow {
  id: number
  name: string
  machine_identifier: string
  activated_at: string
  deactivated_at: string | null
}

interface LicenseDetail {
  id: number
  key: string
  masked_key: string
  status: string
  plan_type: string
  revoked_at: string | null
  created_at: string
  account: { id: number; name: string }
  assignee_email: string | null
  seat: {
    id: number
    source_type: string
    source_description: string
    released: boolean
  }
  devices: DeviceRow[]
}

const TABS = [
  { key: "details", label: "Details" },
  { key: "devices", label: "Devices" },
]

export default function AdminLicenseShow({ license }: { license: LicenseDetail }) {
  const [tab, setTab] = useState("details")

  return (
    <>
      <Head title={`License ${license.masked_key}`}>
        <meta name="description" content="Admin detail for a Flywrite license key." />
        <meta property="og:title" content={`License ${license.masked_key}`} />
        <meta property="og:description" content="Admin detail for a Flywrite license key." />
      </Head>
      <AdminShell>
        <PageHeader
          title="License"
          description={`Issued ${formatDate(license.created_at)} · ${license.plan_type === "lifetime" ? "Lifetime" : "Subscription"} plan.`}
          actions={
            <>
              <LicenseStatusBadge status={license.status} />
              {license.status !== "revoked" && <RevokeDialog license={license} />}
              {license.status === "revoked" && <ReactivateDialog license={license} />}
              <Link href="/admin/licenses" className="text-sm text-ink-muted no-underline hover:text-ink-display">
                Back to licenses
              </Link>
            </>
          }
          tabs={<SubNav items={TABS} active={tab} onChange={setTab} ariaLabel="License sections" />}
        />

        <section className={tab === "details" ? "mt-8" : "hidden"}>
          <div className="max-w-2xl">
            <CopyField value={license.key} ariaLabel="Copy license key" />
          </div>
          <DataTable className="mt-6">
            <DataRow title="Account">
              <Link href={`/admin/accounts/${license.account.id}`}>{license.account.name}</Link>
            </DataRow>
            <DataRow title="Assigned to">{license.assignee_email ?? "Unassigned"}</DataRow>
            <DataRow title="Seat source">{license.seat.source_description}</DataRow>
            <DataRow title="Seat state">
              {license.seat.released ? <Badge tone="muted">Released</Badge> : <Badge tone="neutral">Active</Badge>}
            </DataRow>
            <DataRow title="Revoked">
              {license.revoked_at ? formatDateTime(license.revoked_at) : "No"}
            </DataRow>
          </DataTable>
        </section>

        <section className={tab === "devices" ? "mt-8" : "hidden"}>
          {license.devices.length === 0 ? (
            <p className="mt-4 text-sm text-ink-muted">No devices have activated this license.</p>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-lg border border-hairline">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hairline bg-surface text-left">
                    <th className="px-4 py-3 font-medium text-ink-muted">Device</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Machine ID</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Activated</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Deactivated</th>
                  </tr>
                </thead>
                <tbody>
                  {license.devices.map((device) => (
                    <tr key={device.id} className="border-b border-hairline last:border-0">
                      <td className="px-4 py-3">{device.name}</td>
                      <td className="px-4 py-3">
                        <code className="font-mono text-xs">{device.machine_identifier}</code>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(device.activated_at)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {device.deactivated_at ? formatDate(device.deactivated_at) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </AdminShell>
    </>
  )
}

function RevokeDialog({ license }: { license: LicenseDetail }) {
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="danger" size="sm">
          Revoke
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Revoke this license?</DialogTitle>
          <DialogDescription>
            The key {license.masked_key} will stop validating immediately on all devices. This is
            for abuse cases — refunds and cancellations are handled automatically by Stripe
            webhooks.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              router.post(`/admin/licenses/${license.id}/revoke`, {}, { onSuccess: () => setOpen(false) })
            }
          >
            Revoke license
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ReactivateDialog({ license }: { license: LicenseDetail }) {
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">
          Reactivate
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Reactivate this license?</DialogTitle>
          <DialogDescription>
            This clears the revocation on {license.masked_key}. Only use this in rare cases — to
            undo a mistaken revoke or handle another special circumstance. The key returns to the
            status derived from its billing source (a canceled subscription still shows as lapsed),
            and devices resume validating on their next check-in.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              router.post(`/admin/licenses/${license.id}/reactivate`, {}, { onSuccess: () => setOpen(false) })
            }
          >
            Reactivate license
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
